import { supabase } from "./supabase";
import type { InvitationPhoto } from "../types/invitation";

export const MAX_INVITATION_PHOTOS = 6;
export function validateInvitationPhoto(file: File) {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
    throw new Error("Choose a JPG, PNG or WebP photo.");
  if (!file.size || file.size > 10 * 1024 * 1024)
    throw new Error("Each photo must be between 1 byte and 10 MB.");
}

export async function uploadInvitationPhoto(
  file: File,
): Promise<InvitationPhoto> {
  validateInvitationPhoto(file);
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user)
    throw new Error("Please sign in again before uploading photos.");
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      "This photo could not be opened. Try another JPG, PNG or WebP file.",
    );
  }
  let blob: Blob;
  try {
    if (
      !bitmap.width ||
      !bitmap.height ||
      bitmap.width * bitmap.height > 40_000_000
    )
      throw new Error("Choose a photo smaller than 40 megapixels.");
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context)
      throw new Error("Unable to prepare this photo. Please try again.");
    context.fillStyle = "#fffaf5";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    // Re-encoding shrinks the image and omits the original file's location/EXIF metadata.
    blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (result) =>
          result
            ? resolve(result)
            : reject(new Error("Unable to prepare this photo.")),
        "image/jpeg",
        0.85,
      ),
    );
  } finally {
    bitmap.close();
  }
  const id = crypto.randomUUID();
  // Reuse the existing authenticated registry-image upload policy without overwriting files.
  const path = `backgrounds/${user.id}/invitations/${id}.jpg`;
  const storage = supabase.storage.from("registry-images");
  const { error } = await storage.upload(path, blob, {
    contentType: "image/jpeg",
    cacheControl: "31536000",
    upsert: false,
  });
  if (error)
    throw new Error(
      "Photo upload failed. Your saved invitation has not changed. Please try again.",
    );
  const {
    data: { publicUrl },
  } = storage.getPublicUrl(path);
  return { id, url: publicUrl, caption: "" };
}
